import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';

interface SiteAuditPortalSession {
  shareCode: string;
  websiteId: string;
  brandId: string;
  clientEmail: string;
  websiteUrl: string;
  isValid: boolean;
}

interface SiteAuditPortalContextType {
  session: SiteAuditPortalSession | null;
  isLoading: boolean;
  validateAccess: (shareCode: string, clientEmail: string, accessPin: string) => Promise<boolean>;
  logout: () => void;
}

const SiteAuditPortalContext = createContext<SiteAuditPortalContextType | undefined>(undefined);

const STORAGE_KEY = 'siteaudit-portal-session';

export const SiteAuditPortalProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [session, setSession] = useState<SiteAuditPortalSession | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Check for existing session
    const storedSession = localStorage.getItem(STORAGE_KEY);
    if (storedSession) {
      try {
        const parsedSession = JSON.parse(storedSession);
        setSession(parsedSession);
      } catch (error) {
        console.error('Error parsing stored session:', error);
        localStorage.removeItem(STORAGE_KEY);
      }
    }
    setIsLoading(false);
  }, []);

  const validateAccess = async (
    shareCode: string,
    clientEmail: string,
    accessPin: string
  ): Promise<boolean> => {
    try {
      const { data, error } = await supabase.rpc('validate_siteaudit_access', {
        p_share_code: shareCode,
        p_client_email: clientEmail,
        p_access_pin: accessPin
      });

      if (error) throw error;

      const result = data as { valid?: boolean; website_id?: string; brand_id?: string; website_url?: string; client_email?: string } | null;

      if (result?.valid) {
        const newSession: SiteAuditPortalSession = {
          shareCode,
          websiteId: result.website_id!,
          brandId: result.brand_id!,
          clientEmail: result.client_email!,
          websiteUrl: result.website_url!,
          isValid: true
        };
        
        setSession(newSession);
        localStorage.setItem(STORAGE_KEY, JSON.stringify(newSession));
        return true;
      }
      
      return false;
    } catch (error) {
      console.error('Error validating access:', error);
      return false;
    }
  };

  const logout = () => {
    setSession(null);
    localStorage.removeItem(STORAGE_KEY);
  };

  return (
    <SiteAuditPortalContext.Provider value={{ session, isLoading, validateAccess, logout }}>
      {children}
    </SiteAuditPortalContext.Provider>
  );
};

export const useSiteAuditPortal = () => {
  const context = useContext(SiteAuditPortalContext);
  if (context === undefined) {
    throw new Error('useSiteAuditPortal must be used within a SiteAuditPortalProvider');
  }
  return context;
};
